/*
===============================================================================================================
                                 Copyright 2022-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

const ruleMappings = {
  productCategory: {
    localeString: 'rules.productCategory',
    value: '$.categories',
  },
  productSKU: {
    localeString: 'rules.productSKU',
    value: '$.sku',
  },
  productTags: {
    localeString: 'rules.productTags',
    value: '$.tags',
  },
  productBrands: {
    localeString: 'rules.productBrands',
    value: '$.brands',
  },
  productPrice: {
    localeString: 'rules.productPrice',
    value: '$.price',
  },
  purchaseDateFact: {
    localeString: 'rules.orderDate',
    value: '$.purchaseDate',
  },
  returnWindowDaysFact: {
    localeString: 'rules.returnDate',
    value: '$.returnWindowDays',
  },
  equalsOperator: {
    localeString: 'rules.equals',
    value: 'fullMatch',
  },
  notEqualsOperator: {
    localeString: 'rules.notEquals',
    value: 'notFullMatch',
  },
  containsOperator: {
    localeString: 'rules.contains',
    value: 'partialMatch',
  },
  greaterThanOperator: {
    localeString: 'rules.greaterThan',
    value: 'greaterThan',
  },
  lessThanOperator: {
    localeString: 'rules.lessThan',
    value: 'lessThan',
  },
  betweenOperator: {
    localeString: 'rules.between',
    value: 'between',
  },
  betweenNumbersOperator: {
    localeString: 'rules.between',
    value: 'betweenNumbers',
  },
  containsOnlyOperator: {
    localeString: 'rules.containsOnly',
    value: 'containsOnly',
  },
  purchaseDateAction: {
    localeString: 'rules.orderDate',
    value: 'purchaseDate',
  },
  area: {
    localeString: 'rules.area',
    value: '$.area',
  },
  returnReason: {
    localeString: 'rules.returnReason',
    value: '$.returnReasonCode',
  },
  returnMethod: {
    localeString: 'rules.returnMethod',
    value: 'returnMethod',
  },
  customerTags: {
    localeString: 'rules.customerTags',
    value: 'tags',
  },
  warehouseLocationId: {
    localeString: 'rules.returnWarehouse',
    value: '$.locationId',
  },
};

export const conditionFactMapping = {
  '$.categories': 'product',
  '$.sku': 'product',
  '$.tags': 'product',
  '$.price': 'product',
  '$.purchaseDate': 'product',
  '$.returnWindowDays': 'product',
  '$.returnReasonCode': 'product',
  '$.area': 'from',
  returnMethod: 'returnMethod',
};

export const conditionUsesPath = (fact) => conditionFactMapping[fact] !== fact;

// this normalises conditions that don't use a path, for the UI,
// by re-attaching the path based on the fact.
export const normalizeConditions = (conditions) => conditions.map((condition) => {
  if (!condition.path && !conditionUsesPath(condition.fact)) {
    return { ...condition, path: ruleMappings[condition.fact]?.value ?? condition.fact };
  }
  if (condition.path && !condition.fact && conditionFactMapping[condition.path]) {
    return { ...condition, fact: conditionFactMapping[condition.path] };
  }
  return condition;
});

export const RuleActionValues = {
  TRUE: 'true',
  FALSE: 'false',
};

export const getReturnMethodOptions = ({ translate }) => [
  { value: 'HOME_PICKUP', title: translate('rules.homePickup') },
  { value: 'CARRIER', title: translate('rules.dropOff') },
  { value: 'STORE_DROP_OFF', title: translate('rules.inStoreReturn') },
];

export const getReturnMethodLabels = ({ translate }) => Object.fromEntries(getReturnMethodOptions({ translate })
  .map(({ value, title }) => [value, title]));

const ruleTranslationMappings = Object.entries(ruleMappings).reduce((total, [, { value, localeString }]) => ({
  ...total,
  [value]: localeString,
}), {});
export const RuleTypes = {
  NON_RETURNABLE_ITEM: 'nonReturnableItem',
  RETURN_ADDRESS: 'returnAddress',
  RETURN_WINDOWS: 'withinReturnWindow',
  ORDER_EXCEPTION: 'exceptionAlwaysReturnableItem',
  CARRIER_RULES: 'returnCarrier',
  REFUND_TRIGGERS: 'refundTrigger',
  REFUND_FEES: 'refundFee',
  RETURNLESS_ITEM: 'returnlessItem',
  REFUND_METHOD: 'refundMethod',
  HOME_PICKUP: 'homePickUp',
  STORE_DROPOFF: 'storeDropOff',
};

const getRuleTranslation = ({ translate, value: key, fact }) => {
  if (fact === 'customer') {
    return translate(ruleMappings.customerTags.localeString);
  }
  if (fact === 'returnAddress' && key === ruleMappings.warehouseLocationId.value) {
    return translate(ruleMappings.warehouseLocationId.localeString);
  }
  if (fact === 'returnMethod') {
    return translate(ruleMappings.returnMethod.localeString);
  }
  const translationKey = ruleTranslationMappings[key];
  return translationKey ? translate(translationKey) : '';
};

export const getWarehouseLocationName = (locationId, warehouses = []) => warehouses.find(
  (warehouse) => warehouse.locationId === locationId,
)?.locationName ?? locationId;

export const getRuleActionValue = ({ rule = {}, warehouses = [] } = {}) => {
  if (rule.ruleType === RuleTypes.RETURN_WINDOWS) {
    const actionPath = rule.rule.event.params.actionValue.returnWindow;
    return {
      days: actionPath?.days,
      from: actionPath?.from,
    };
  }
  if (rule.ruleType === RuleTypes.RETURN_ADDRESS) {
    const warehouse = warehouses.find(
      ({ locationId }) => locationId === rule.rule.event.params.actionValue.locationId,
    );
    return {
      returnTo: warehouse?.locationName,
      locationId: warehouse?.locationId,
    };
  }
  if (rule.ruleType === RuleTypes.CARRIER_RULES) {
    const actionPath = rule.rule.event.params.actionValue.returnCarrier;
    return {
      shouldUse: actionPath?.shouldUse?.toString(),
      carrier: actionPath?.carrier,
      serviceType: actionPath?.serviceType,
    };
  }
  if (rule.ruleType === RuleTypes.REFUND_TRIGGERS) {
    const actionPath = rule.rule.event.params.actionValue.refundTrigger;
    return {
      eventType: actionPath?.eventType,
    };
  }
  if (rule.ruleType === RuleTypes.REFUND_FEES) {
    const actionPath = rule.rule.event.params.actionValue.refundFee;
    return {
      amount: actionPath?.amount,
      name: actionPath?.name,
      currency: actionPath?.currency,
    };
  }
  if (rule.ruleType === RuleTypes.REFUND_METHOD) {
    const actionPath = rule.rule.event.params.actionValue.refundMethods || [];
    return {
      refundMethods: actionPath,
    };
  }
  if (rule.ruleType === RuleTypes.HOME_PICKUP) {
    const actionPath = rule.rule.event.params.actionValue.homePickUp;
    return {
      enabled: actionPath?.enabled,
    };
  }
  if (rule.ruleType === RuleTypes.STORE_DROPOFF) {
    const actionPath = rule.rule.event.params.actionValue.storeDropOff;
    const tags = actionPath?.criteria?.tags?.include;
    const subBrandCompanyIds = actionPath?.criteria?.subBrandCompanyIds?.include;
    let criteriaType;
    let criteriaValues;
    if (subBrandCompanyIds?.length) {
      criteriaType = 'brand';
      criteriaValues = subBrandCompanyIds;
    } else if (tags?.length) {
      criteriaType = 'storeTag';
      criteriaValues = tags;
    }
    return {
      enabled: actionPath?.enabled?.toString(),
      criteriaType,
      criteriaValues,
    };
  }
  return {};
};

/**
 * Generates payload for rules priority update request.
 *
 * @param {Array} rulesList A list of rules
 * @return {Array} List of { ruleId: string, priority: number } entries
 */
export const generateRulePriorityPayload = (rulesList) => {
  const prioritiesArray = rulesList.map((rule) => rule.rule?.priority).sort((a, b) => b - a);
  return rulesList.map((rule, index) => ({ ruleId: rule.ruleId, priority: prioritiesArray[index] }));
};

export default {
  mappings: ruleMappings,
  getRuleTranslation,
  getRuleActionValue,
  getWarehouseLocationName,
  generateRulePriorityPayload,
  RuleTypes,
};
